import { nextTick } from 'vue';

/**
 * Progressive row rendering for a spreadsheet page.
 *
 * The page is split into fixed-size chunks, each with its own DataTable, mounted one per frame.
 * The first chunk is mounted immediately; subsequent chunks are added one per
 * animation frame so the browser can paint between batches.
 * 
 * Keeping completed chunks separate avoids growing a single DataTable as more
 * rows are introduced.
 * 
 * Hosts must provide `currentTemplate`, the rows currently displayed by the page.
 */

const CHUNK_ROWS = 30;

export default {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    data() {
        return {
            // Number of chunks currently mounted
            renderedChunks: 0,
            // chunkFrame cancels; chunkPending says a step is in flight - still true across the
            // nextTick between frames, when chunkFrame is momentarily 0.
            chunkFrame: 0,
            chunkPending: false,
        };
    },
    computed: {
        /** Hosts override this with the rows currently displayed by the page. */
        currentTemplate(): any {
            return null;
        },
        /**
         * Split the current page into fixed-size chunks.
         *
         * Each chunk owns its own row array so completed DataTables can remain
         * unchanged as later chunks are mounted.
         */
        rowChunks(): any[] {
            const template = (this as any).currentTemplate;
            if (template == null || template.length === 0) return [];
            const chunks = [] as any[];
            for (let start = 0; start < template.length; start += CHUNK_ROWS) {
                chunks.push({start: start, rows: template.slice(start, start + CHUNK_ROWS)});
            }
            return chunks;
        },
        visibleChunks(): any[] {
            const self = this as any;
            return self.rowChunks.slice(0, self.renderedChunks);
        },
    },
    watch: {
        // Restart when the host supplies a new page array. `immediate` also handles
        // pages that are already populated when the mixin mounts. An in-place splice keeps the
        // same reference and so never reaches here (handled by rowChunks watcher).
        currentTemplate: {
            handler() { (this as any).startProgressiveRender(); },
            immediate: true,
        },
        // Keep the number of mounted chunks in sync when the page gains or loses rows.
        // A new chunk added after the initial fill must still be mounted.
        rowChunks(chunks: any[]) {
            const self = this as any;
            if (self.renderedChunks > chunks.length) {
                self.renderedChunks = chunks.length;
                return;
            }
            self.scheduleNextChunk();
        },
    },
    // `const self = this as any` throughout: mixin's inferred `this` type does not include its data/computed members
    methods: {
        /**
         * First chunk now, then add one chunk per animation frame. 
         * The full frame between chunks gives the browser a chance to paint before more rows mount.
         */
        startProgressiveRender() {
            const self = this as any;
            self.stopProgressiveRender();
            self.renderedChunks = self.rowChunks.length === 0 ? 0 : 1;
            self.scheduleNextChunk();
        },
        /** Queue the next chunk unless rendering is already queued or all chunks are mounted. */
        scheduleNextChunk() {
            const self = this as any;
            if (self.chunkPending || self.renderedChunks >= self.rowChunks.length) return;
            self.chunkPending = true;
            self.chunkFrame = requestAnimationFrame(() => {
                self.chunkFrame = 0;
                self.chunkPending = false;
                if (self.renderedChunks >= self.rowChunks.length) return;
                self.renderedChunks += 1;
                nextTick(() => self.scheduleNextChunk());
            });
        },
        /**
         * Mount every chunk up to and including the one holding `rowIndex` - chunks mount as a
         * prefix, so a row deep in the page cannot be reached on its own.
         * Returns true when mounting occurred, so callers that need the row in the DOM can wait for
         * the next Vue tick before querying it. `needed` is clamped to the chunk count so an
         * out-of-range index cannot leave a retrying caller looping forever.
         */
        ensureRowRendered(rowIndex: any): boolean {
            const self = this as any;
            const total = self.rowChunks.length;
            if (total === 0 || rowIndex == null || rowIndex < 0) return false;

            const needed = Math.min(Math.floor(rowIndex / CHUNK_ROWS) + 1, total);
            if (needed <= self.renderedChunks) return false;

            self.renderedChunks = needed;
            self.scheduleNextChunk();
            return true;
        },
        stopProgressiveRender() {
            const self = this as any;
            if (self.chunkFrame !== 0) {
                cancelAnimationFrame(self.chunkFrame);
                self.chunkFrame = 0;
            }
            self.chunkPending = false;
        },
    },
};
